import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '../ui/Button';
import { DatePicker } from '../utils';
import { useCurrency } from '../../utils/globalCurrency';
import downloadIcon from '../../assets/images/download.png';
import printIcon from '../../assets/images/share.png';
import { MappedOverallTradingAccountData } from '../Accounts/use-overall-trading-account';

interface OverallTradingAccountTemplateProps {
  title: string;
  description?: string;
  data: MappedOverallTradingAccountData | null;
  loading?: boolean;
  onExport?: () => void;
  onPrint?: () => void;
  onExportImage?: () => void;
  onExportPDF?: () => void;
  onExportExcel?: () => void;
  showDateRange?: boolean;
  startDate?: string;
  endDate?: string;
  onDateRangeChanged?: (startDate?: string, endDate?: string) => void;
  debitSideTitle?: string;
  creditSideTitle?: string;
  emptyMessage?: string;
  entryCount?: number;
  extraContent?: React.ReactNode;
}

interface TradingAccountRow {
  particular: string;
  amountCol1: number | undefined;
  amountCol2: number | undefined;
  operation: 'None' | 'Add' | 'Less' | 'Result' | 'Result(=)' | 'Result(-)';
}

const OverallTradingAccountTemplate: React.FC<OverallTradingAccountTemplateProps> = ({
  title,
  description,
  data,
  loading = false,
  onExport,
  onPrint,
  onExportImage,
  onExportPDF,
  onExportExcel,
  showDateRange = true,
  startDate: propStartDate,
  endDate: propEndDate,
  onDateRangeChanged,
  debitSideTitle = 'DR',
  creditSideTitle = 'CR',
  emptyMessage = 'No trading account data available',
  entryCount,
  extraContent,
}) => {
  const { format } = useCurrency();
  const [startDate, setStartDate] = useState(propStartDate || '');
  const [endDate, setEndDate] = useState(propEndDate || '');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  const hasExportDropdown = !!(onExportImage || onExportPDF || onExportExcel);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  useEffect(() => {
    if ((startDate || endDate) && onDateRangeChanged) {
      onDateRangeChanged(startDate || undefined, endDate || undefined);
    }
  }, [startDate, endDate, onDateRangeChanged]);

    // Organize trading account data in proper format
  const organizedData = useMemo(() => {
    if (!data) return { debitRows: [], creditRows: [], totalDr: 0, totalCr: 0 };

    const { debitSide, creditSide } = data;

    // Helper function to get amount from items array
    const getAmount = (items: Array<{ name: string; amount: number }>, searchName: string): number => {
      const normalizedSearch = searchName.toLowerCase().trim();
      const item = items.find(item =>
        item.name.toLowerCase().trim() === normalizedSearch
      );
      return item?.amount || 0;
    };

    // Get values from debit side
    const openingStock = getAmount(debitSide.items, 'Opening Stock');
    const purchases = getAmount(debitSide.items, 'Purchases');
    const carriageInwards = getAmount(debitSide.items, 'Carriage Inwards');
    const returnOutwards = getAmount(debitSide.items, 'Return Outwards');
    const costOfGoodsAvailable = getAmount(debitSide.items, 'Cost of Goods Available');
    const closingStock = getAmount(debitSide.items, 'Closing Stock');
    const costOfGoodsSold = getAmount(debitSide.items, 'Cost of Goods Sold');
    const grossProfit = getAmount(debitSide.items, 'Gross Profit');
    const totalDr = debitSide.total || 0;

    // Get values from credit side
    const sales = getAmount(creditSide.items, 'Sales');
    const returnInwards = getAmount(creditSide.items, 'Return Inwards');
    const carriageOutwards = getAmount(creditSide.items, 'Carriage Outwards');
    const totalCr = creditSide.total || 0;



    // Debit side rows (8 rows)
    const debitRows: TradingAccountRow[] = [
      { particular: 'Opening Stock', amountCol1: openingStock, amountCol2: undefined, operation: 'None' },
      { particular: 'Purchases', amountCol1: purchases, amountCol2: undefined, operation: 'Add' },
      { particular: 'Carriage Inwards', amountCol1: carriageInwards, amountCol2: undefined, operation: 'Add' },
      { particular: 'Return Outwards', amountCol1: returnOutwards, amountCol2: undefined, operation: 'Less' },
      { particular: 'Cost of Goods Available', amountCol1: undefined, amountCol2: costOfGoodsAvailable, operation: 'Result(=)' },
      { particular: 'Closing Stock', amountCol1: undefined, amountCol2: closingStock, operation: 'Less' },
      { particular: 'Cost of Goods Sold', amountCol1: undefined, amountCol2: costOfGoodsSold, operation: 'Result(-)' },
      { particular: 'Gross Profit', amountCol1: undefined, amountCol2: grossProfit, operation: 'Result(-)' },
    ];

    // Credit side rows - need 9 rows to match debit side
    const creditRows: TradingAccountRow[] = [
      { particular: 'Sales', amountCol1: sales, amountCol2: undefined, operation: 'None' },
      { particular: 'Return Inwards', amountCol1: returnInwards, amountCol2: undefined, operation: 'Less' },
      { particular: 'Carriage Outwards', amountCol1: carriageOutwards, amountCol2: undefined, operation: 'Less' },
      { particular: '', amountCol1: undefined, amountCol2: undefined, operation: 'Result(-)' },
      // Add 4 empty rows to match debit side total of 9 rows
      { particular: '', amountCol1: undefined, amountCol2: undefined, operation: 'None' },
      { particular: '', amountCol1: undefined, amountCol2: undefined, operation: 'None' },
      { particular: '', amountCol1: undefined, amountCol2: undefined, operation: 'None' },
      { particular: '', amountCol1: undefined, amountCol2: undefined, operation: 'None' },
    ];

    return {
      debitRows,
      creditRows,
      totalDr,
      totalCr
    };
  }, [data]);

    const renderSide = (isDebitSide: boolean) => {
    const rows = isDebitSide ? organizedData.debitRows : organizedData.creditRows;
    const total = isDebitSide ? organizedData.totalDr : organizedData.totalCr;
    const sideTitle = isDebitSide ? debitSideTitle : creditSideTitle;
    const isTotalInCol2 = true; // Both Total(Dr) and Total(Cr) in column 2

    return (
      <div className="flex-1 flex flex-col">
        <div className={`${isDebitSide ? 'text-start' : 'text-end'} mb-2 text-dark text-sm font-medium`}>
          {sideTitle}
        </div>

        <div className="grid grid-cols-12 gap-2 mb-3 pb-2 border-b border-grey_light text-grey_dark text-sm">
          <div className="col-span-6"></div>
          <div className="col-span-3 text-right"></div>
          <div className="col-span-3 text-right"></div>
        </div>

        <div className="flex-1">
          {rows.length === 0 ? (
            <div className="text-center py-6 text-grey_dark text-sm">
              {emptyMessage}
            </div>
          ) : (
            <div>
              {rows.map((row, index) => {
                // Determine operation symbol
                let operationSymbol = '';
                if (row.operation === 'Add') operationSymbol = '+ ';
                if (row.operation === 'Less') operationSymbol = '- ';
                if (row.operation === 'Result(=)' || row.operation === 'Result(-)') operationSymbol = '= ';

                return (
                  <div
                    key={`${row.particular}-${index}`}
                    className="grid grid-cols-12 gap-2 min-h-[40px] items-center"
                  >
                    <div className="col-span-6 text-sm">
                      {row.particular}
                    </div>
                    <div className="col-span-3 text-right text-sm">
                      {row.amountCol1 !== undefined ? `${operationSymbol}${format(row.amountCol1)}` : ''}
                    </div>
                    <div className="col-span-3 text-right text-sm">
                      {row.amountCol2 !== undefined ? `${operationSymbol}${format(row.amountCol2)}` : ''}
                    </div>
                  </div>
                );
              })}
              
              {/* Total row with divider line */}
              <div className="border-t border-grey_light mt-2">
                <div className="grid grid-cols-12 gap-2 min-h-[40px] items-center">
                  <div className="col-span-6 text-sm">
                    {/* Empty for total */}
                  </div>
                  <div className="col-span-3 text-right text-sm font-medium">
                    {!isTotalInCol2 ? `= ${format(total)}` : ''}
                  </div>
                  <div className="col-span-3 text-right text-sm font-medium">
                    {isTotalInCol2 ? `= ${format(total)}` : ''}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-shink-from-navbar h-fill-from-navbar px-2 bg-bg_grey_light text-dark overflow-y-scroll">
        <div className="max-w-7xl mx-auto">
          <div className="p-6 bg-light rounded-lg flex justify-center items-center">
            <div className="text-grey_dark">Loading trading account...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-shink-from-navbar h-fill-from-navbar px-2 bg-bg_grey_light text-dark overflow-y-scroll">
      <div className="max-w-7xl mx-auto">
        <div className="p-6 pb-3 bg-light rounded-t-lg flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
          <div className="w-full sm:w-auto mt-2">
            <h3 className="text-xl">{title}</h3>
            {description && (
              <p className="text-grey_dark text-sm mt-1">{description}</p>
            )}
          </div>
          <div className="flex gap-2 mt-4 sm:mt-0 text-sm">
            {hasExportDropdown ? (
              <div className="relative" ref={exportDropdownRef}>
                <Button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  variant="google"
                  className="flex items-center text-grey_dark"
                >
                  <img
                    src={downloadIcon}
                    alt="Export"
                    className="object-contain w-6 h-6 mr-2"
                  />
                  Export
                </Button>
                {showExportMenu && (
                  <div className="absolute right-0 top-10 bg-light border border-grey_light rounded-lg shadow-lg z-50 min-w-[160px]">
                    {onExportImage && (
                      <button
                        onClick={() => { setShowExportMenu(false); onExportImage(); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-dark hover:bg-bg_grey_light transition-colors rounded-t-lg flex items-center gap-2"
                      >
                        <svg className="w-4 h-4 text-grey_dark" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        Image (PNG)
                      </button>
                    )}
                    {onExportPDF && (
                      <button
                        onClick={() => { setShowExportMenu(false); onExportPDF(); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-dark hover:bg-bg_grey_light transition-colors flex items-center gap-2"
                      >
                        <svg className="w-4 h-4 text-grey_dark" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                        PDF
                      </button>
                    )}
                    {onExportExcel && (
                      <button
                        onClick={() => { setShowExportMenu(false); onExportExcel(); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-dark hover:bg-bg_grey_light transition-colors rounded-b-lg flex items-center gap-2"
                      >
                        <svg className="w-4 h-4 text-grey_dark" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                        Excel
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : onExport ? (
              <Button
                onClick={onExport}
                variant="google"
                className="flex items-center text-grey_dark"
              >
                <img
                  src={downloadIcon}
                  alt="Export"
                  className="object-contain w-6 h-6 mr-2"
                />
                Export
              </Button>
            ) : null}
            {onPrint && (
              <Button
                onClick={onPrint}
                variant="google"
                className="flex items-center text-grey_dark"
              >
                <img
                  src={printIcon}
                  alt="Print"
                  className="object-contain w-6 h-6 mr-2"
                />
                Print
              </Button>
            )}
          </div>
        </div>

        {showDateRange && (
          <div className="bg-light rounded-lg p-6 mb-2">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex justify-between">
                <div className="mr-2">
                  <DatePicker
                    id="start-date"
                    value={startDate}
                    onChange={setStartDate}
                    label="Start Date"
                    type="date"
                    className="mr-3"
                  />
                </div>
                <div className="ml-2">
                  <DatePicker
                    id="end-date"
                    value={endDate}
                    onChange={setEndDate}
                    label="End Date"
                    type="date"
                  />
                </div>
              </div>
              {entryCount !== undefined && (
                <div className="text-sm text-grey_dark">
                  {entryCount} of {entryCount} entries
                </div>
              )}
            </div>
          </div>
        )}

        <div className="bg-light rounded-lg p-4 sm:p-6 mb-4">
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-6">
            {renderSide(true)}
            <div className="hidden lg:block w-px bg-grey_light"></div>
            {renderSide(false)}
          </div>
        </div>

        {extraContent}
      </div>
    </div>
  );
};

export default OverallTradingAccountTemplate;
